import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCheck, Bell } from 'lucide-react';
import { notificationsApi } from '../services/resources';
import { getApiErrorMessage } from '../services/api';
import { ILMSPageHeader } from '../components/ILMSPageHeader';
import { ILMSCard } from '../components/ILMSCard';
import { ILMSButton } from '../components/ILMSButton';
import { ILMSAlert } from '../components/ILMSAlert';
import { ILMSStatusBadge } from '../components/ILMSStatusBadge';
import { ILMSEmptyState } from '../components/ILMSEmptyState';
import { ILMSTableSkeleton } from '../components/ILMSLoadingState';
import { DEFAULT_PAGE_SIZE, ILMSPagination } from '../components/ILMSPagination';
import { cn } from '../utils/cn';
import { formatRelativeTime } from '../utils/format';
import type { ListQueryParams, Notification } from '../types';

export default function NotificationsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const listQuery = useQuery({
    queryKey: ['notifications', page, pageSize, filter],
    queryFn: () => notificationsApi.list({ page, page_size: pageSize, status_filter: filter === 'unread' ? 'Unread' : undefined } as ListQueryParams),
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const items = listQuery.data?.items ?? [];

  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      <ILMSPageHeader
        title="Notifications"
        subtitle="Alerts and reminders for tasks that need your attention."
        actions={
          <ILMSButton variant="outline" icon={<CheckCheck />} onClick={() => markAllReadMutation.mutate()} loading={markAllReadMutation.isPending}>
            Mark all as read
          </ILMSButton>
        }
      />

      <div className="flex gap-2">
        <button
          onClick={() => { setFilter('all'); setPage(1); }}
          className={cn('h-9 px-3 rounded-md text-sm font-medium', filter === 'all' ? 'bg-brand-green-700 text-white' : 'bg-white border border-ink-300/30 text-ink-700')}
        >
          All
        </button>
        <button
          onClick={() => { setFilter('unread'); setPage(1); }}
          className={cn('h-9 px-3 rounded-md text-sm font-medium', filter === 'unread' ? 'bg-brand-green-700 text-white' : 'bg-white border border-ink-300/30 text-ink-700')}
        >
          Unread
        </button>
      </div>

      <ILMSCard padded={false}>
        {listQuery.isLoading ? (
          <div className="p-5"><ILMSTableSkeleton rows={5} columns={3} /></div>
        ) : listQuery.isError ? (
          <div className="p-5">
            <ILMSAlert tone="critical" title="Unable to load notifications">
              {getApiErrorMessage(listQuery.error)}
              <div className="mt-2">
                <ILMSButton size="sm" variant="outline" onClick={() => listQuery.refetch()}>Retry</ILMSButton>
              </div>
            </ILMSAlert>
          </div>
        ) : items.length === 0 ? (
          <ILMSEmptyState icon={<Bell className="h-6 w-6" />} title="You're all caught up" description="There are no notifications to show right now." />
        ) : (
          <ul className="divide-y divide-ink-300/10">
            {items.map((n: Notification) => (
              <li key={n.id} className={cn('p-4 sm:p-5 flex gap-3', n.status === 'Unread' && 'bg-brand-green-50/30')}>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <ILMSStatusBadge status={n.priority} size="sm" />
                    <p className="font-semibold text-ink-900">{n.title}</p>
                  </div>
                  <p className="text-secondary text-ink-700 mt-1">{n.message}</p>
                  <p className="text-helper-text text-ink-400 mt-1">{formatRelativeTime(n.created_at)}</p>
                </div>
                {n.status === 'Unread' && (
                  <ILMSButton
                    size="sm"
                    variant="ghost"
                    onClick={() => markReadMutation.mutate(n.id)}
                    loading={markReadMutation.isPending && markReadMutation.variables === n.id}
                  >
                    Mark as read
                  </ILMSButton>
                )}
              </li>
            ))}
          </ul>
        )}
        {!listQuery.isLoading && !listQuery.isError && (listQuery.data?.total ?? 0) > 0 && (
          <div className="p-4 sm:p-5 pt-0">
            <ILMSPagination page={page} pageSize={pageSize} total={listQuery.data?.total ?? 0} onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
          </div>
        )}
      </ILMSCard>
    </div>
  );
}
